import React, {
    createContext, useContext, useEffect, useMemo, useState,
} from 'react';

import { inGamecenter } from '@vkplay/shared';

import AuthContext from 'contexts/auth';

const FeedbackContext = createContext();

const DELAY_MS_7_DAYS = 604800000;
const IS_TEST = window.location.search.includes('testFeedback');
const IS_TEST_NO_DELAY = window.location.search.includes('testFeedbackNoDelay');

const getDelay = (regTs?: number) => {
    if (!regTs) {
        return DELAY_MS_7_DAYS;
    }

    const ts = Date.now() - (regTs * 1000);

    return ts > DELAY_MS_7_DAYS ? 0 : DELAY_MS_7_DAYS - ts;
};

const getSurveys = (regTs?: number) => [
    {
        surveyId: '38',
        delay: IS_TEST_NO_DELAY ? 0 : getDelay(regTs),
        permille: IS_TEST ? 1000 : 40,
        repeatAfterDays: 180,
        triggerEvents: [
            { type: 'story' },
        ],
    },
    {
        surveyId: '39',
        delay: IS_TEST_NO_DELAY ? 0 : getDelay(regTs),
        permille: IS_TEST ? 1000 : 100,
        repeatAfterDays: 180,
        triggerEvents: [
            { type: 'home' },
        ],
    },
];

const isRuStoreUser = window.location.search.includes('mt_link_id=jsc1s8');

function FeedbackProvider({ children }) {
    const { user, isUnauthorized } = useContext(AuthContext);

    const [isInited, setInited] = useState(false);
    const [events, setEvents] = useState([]);

    useEffect(() => {
        console.debug('FEEDBACK USER', user, { isUnauthorized });

        const { user_id, client_profile_fields } = user || {};
        const { reg_ts } = client_profile_fields || {};

        if (isRuStoreUser || !window.Feedback || (!isUnauthorized && (!user_id || !reg_ts)) || isInited) {
            return;
        }

        initFeedback(user_id, reg_ts);
    }, [user, isUnauthorized]);

    useEffect(() => {
        if (!window.FeedbackEvents || !isInited || !events.length) {
            return;
        }

        events.forEach((event) => {
            console.debug('FEEDBACK EVENT ARRAY', event.type);
            console.debug('FEEDBACK', window.FeedbackEvents.triggerWithDelay);
            window.FeedbackEvents.triggerWithDelay(event.type, event.delay);
        });

        setEvents([]);
    }, [isInited, events]);

    function initFeedback(user_id: number, reg_ts: string): void {
        const surveys = getSurveys(+reg_ts);
        const surveyIds = surveys.map((item) => item.surveyId);

        console.debug('FEEDBACK INIT');

        window.Feedback.init({
            styles: inGamecenter ? {
                right: '16px',
                left: 'initial',
                zIndex: 99999,
            } : {
                zIndex: 99999,
            },
            clientId: user_id,
            surveys,
            projectName: inGamecenter ? 'vkplay_gamecenter' : 'vkplay_desktop_web',
            quarantine: {
                quarantineByShowDays: 7,
                quarantineByAnswerDays: 180,
                quarantineIds: surveyIds,
            },
        });

        setInited(true);
    }

    function triggerEvent(type, delay) {
        if (isInited) {
            console.debug('FEEDBACK EVENT', type);
            window.FeedbackEvents.triggerWithDelay(type, delay);

            return;
        }

        setEvents([
            ...events,
            {
                type,
                delay,
            },
        ]);
    }

    const value = useMemo(() => ({
        triggerEvent,
    }), [triggerEvent]);

    return (
        <FeedbackContext.Provider value={value}>
            {children}
        </FeedbackContext.Provider>
    );
}

function withFeedback(Component) {
    return (props) => (
        <FeedbackProvider>
            <Component {...props} />
        </FeedbackProvider>
    );
}

function useFeedback() {
    const context = useContext(FeedbackContext);

    if (context === undefined) {
        throw new Error('useFeedback must be used within a FeedbackProvider');
    }

    return context;
}

export { useFeedback, withFeedback };
